import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Eye, Mail, Trash2, MessageCircle, Paperclip, X } from "lucide-react";
import { toB64 } from "@/lib/pdf";
import { api, errMsg, fmtDate, fmtEur, waPhone } from "@/lib/api";
import { pptoPdf } from "@/lib/pptoPdf";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const avisoTxt = (patient, to, conPpto) => `Hola, os acabamos de enviar por correo electrónico${to ? ` (${to})` : ""} el informe de ${patient.nombre}${conPpto ? " junto con el presupuesto" : ""}. Revisadlo cuando podáis y, si tenéis cualquier duda, escribidnos por aquí o llamadnos.\n\nUn saludo,\nSalud y Visión`;

export const avisarWa = (patient, to, conPpto) => window.open(`https://wa.me/${waPhone(patient.telefono || "")}?text=${encodeURIComponent(avisoTxt(patient, to, conPpto))}`, "_blank");

const AvisoWa = ({ patient, to, conPpto, onClose }) => (
  <div className="space-y-3" data-testid="informe-aviso-wa">
    <p className="text-sm text-emerald-700">Informe enviado a {to}. Estado: Enviado.</p>
    <p className="text-xs text-slate-500">¿Avisamos a la familia por WhatsApp de que ya lo tienen en su correo?</p>
    <p className="whitespace-pre-line rounded bg-slate-50 p-2 text-xs text-slate-700">{avisoTxt(patient, to, conPpto)}</p>
    {!patient.telefono && <p className="text-xs text-amber-700">Este paciente no tiene teléfono en su ficha.</p>}
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={onClose} data-testid="informe-aviso-cerrar">Ahora no</Button>
      <Button disabled={!patient.telefono} onClick={() => { avisarWa(patient, to, conPpto); onClose(); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="informe-aviso-wa-btn"><MessageCircle className="h-4 w-4 mr-1" />Avisar por WhatsApp</Button>
    </div>
  </div>
);

const PptoChecks = ({ pptos, sel, setSel }) => (pptos.length ? (
  <div className="rounded border border-slate-200 p-2" data-testid="informe-pptos">
    <p className="text-xs font-medium text-slate-700 mb-1">Adjuntar también el presupuesto</p>
    {pptos.map((b) => (
      <label key={b.id} className="flex items-center gap-2 py-0.5 text-xs text-slate-700 cursor-pointer">
        <Checkbox checked={sel.includes(b.id)} onCheckedChange={(c) => setSel(c ? [...sel, b.id] : sel.filter((x) => x !== b.id))} data-testid={`informe-ppto-${b.id}`} />
        Presupuesto {b.fecha ? fmtDate(b.fecha) : ""} · {fmtEur(b.total)} · {b.estado}
      </label>
    ))}
  </div>
) : null);

const OtrosPdf = ({ otros, setOtros }) => {
  const ref = useRef(null);
  const add = async (files) => {
    const ok = [...files].filter((f) => f.type === "application/pdf");
    if (ok.length < files.length) toast.error("Solo se pueden adjuntar PDF");
    if ([...otros, ...ok].reduce((s, f) => s + f.size, 0) > 15 * 1024 * 1024) return toast.error("Los adjuntos pesan demasiado (máx. 15 MB)");
    setOtros([...otros, ...ok].slice(0, 5));
    ref.current.value = "";
  };
  return (
    <div className="rounded border border-slate-200 p-2" data-testid="informe-otros">
      <div className="flex items-center">
        <p className="flex-1 text-xs font-medium text-slate-700">Otros PDF (justificante, hoja de ejercicios…)</p>
        <input ref={ref} type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => add(e.target.files)} data-testid="informe-otros-input" />
        <Button size="sm" variant="ghost" className="h-7" onClick={() => ref.current.click()} data-testid="informe-otros-btn"><Paperclip className="h-3.5 w-3.5 mr-1" />Añadir PDF</Button>
      </div>
      {otros.map((f, i) => (
        <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`informe-otro-${i}`}>
          <span className="flex-1 truncate">{f.name}</span>
          <button onClick={() => setOtros(otros.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`informe-otro-quitar-${i}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
};

export const EnviarDialog = ({ inf, patient, onClose, onDone, paso, onSkip }) => {
  const [otros, setOtros] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const [pptos, setPptos] = useState([]);
  const [sel, setSel] = useState([]);
  const [hecho, setHecho] = useState(null);
  useEffect(() => {
    setF(inf ? { to: patient.email || "", asunto: `Informe de ${patient.nombre} · Salud y Visión`, cuerpo: `Hola,\n\nOs adjuntamos el informe de ${patient.nombre}.\n\nCualquier duda, estamos a vuestra disposición.\n\nUn saludo,\nSalud y Visión` } : null);
    setSel([]);
    setOtros([]);
    setHecho(null);
    if (inf) api.get("/presupuestos", { params: { patient_id: patient.id } }).then((r) => setPptos(r.data));
  }, [inf, patient]);
  if (!f) return null;
  const cerrar = () => (hecho ? onDone() : onClose());
  const enviar = async () => {
    try {
      const presupuestos = [];
      for (const bid of sel) {
        setBusy("Preparando presupuesto…");
        presupuestos.push(await pptoPdf(patient.id, bid));
      }
      setBusy("Enviando…");
      const adj = await Promise.all(otros.map(async (o) => ({ pdf_base64: await toB64(o), filename: o.name })));
      await api.post(`/informes-pdf/${inf.id}/enviar`, { ...f, presupuestos, otros: adj });
      toast.success(`Informe enviado a ${f.to}. Estado: Enviado`);
      setHecho({ to: f.to, conPpto: presupuestos.length > 0 });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  return (
    <Dialog open={!!inf} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-lg" data-testid="informe-enviar-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Enviar informe por email{paso ? <span className="ml-2 text-xs font-normal text-slate-500" data-testid="informe-enviar-paso">{paso} · {patient.nombre}</span> : null}</DialogTitle></DialogHeader>
        {hecho ? <AvisoWa patient={patient} to={hecho.to} conPpto={hecho.conPpto} onClose={onDone} /> : <>
        <p className="text-xs text-slate-500 -mt-2">Adjunto: {inf.filename}</p>
        <Input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="email de la familia" data-testid="informe-enviar-to" />
        <Input value={f.asunto} onChange={(e) => setF({ ...f, asunto: e.target.value })} data-testid="informe-enviar-asunto" />
        <Textarea rows={7} value={f.cuerpo} onChange={(e) => setF({ ...f, cuerpo: e.target.value })} data-testid="informe-enviar-cuerpo" />
        <PptoChecks pptos={pptos} sel={sel} setSel={setSel} />
        <OtrosPdf otros={otros} setOtros={setOtros} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{onSkip ? "Parar" : "Cancelar"}</Button>
          {onSkip && <Button variant="outline" onClick={onSkip} data-testid="informe-enviar-saltar">Saltar este</Button>}
          <Button disabled={!!busy || !f.to.trim()} onClick={enviar} data-testid="informe-enviar-confirmar" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{busy || "Enviar"}</Button>
        </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};

const abrirPdf = async (url) => {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(url, { responseType: "blob" });
    w.location.href = URL.createObjectURL(r.data);
  } catch (e) {
    w.close();
    toast.error(errMsg(e));
  }
};

const Envios = ({ inf }) => (inf.envios?.length ? (
  <div className="w-full pl-2 border-l-2 border-emerald-100 space-y-0.5" data-testid={`informe-envios-${inf.id}`}>
    {inf.envios.map((e, k) => (
      <div key={k} className="text-[11px] text-slate-500">
        <span className="text-slate-600">{fmtDate(e.at.slice(0, 10))} a {e.to}:</span> informe
        {e.presupuestos.map((n) => <span key={n}> · {n}</span>)}
        {e.adjuntos.map((a) => (
          <span key={a.id}> · {a.storage_path ? <button className="text-[#1E3A8A] underline" onClick={() => abrirPdf(`/informes-pdf/${inf.id}/adjuntos/${a.id}`)} data-testid={`informe-adjunto-${a.id}`}>{a.filename}</button> : a.filename}</span>
        ))}
      </div>
    ))}
  </div>
) : null);

export const InformesPdf = ({ patient, onChange }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [env, setEnv] = useState(null);
  const ref = useRef(null);
  const load = useCallback(() => {
    api.get(`/patients/${patient.id}/informes-pdf`).then((r) => setRows(r.data));
  }, [patient.id]);
  useEffect(() => {
    load();
  }, [load]);
  const subir = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Solo PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/patients/${patient.id}/informes-pdf`, fd);
      toast.success("Informe subido");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    ref.current.value = "";
  };
  const ver = (inf) => abrirPdf(`/informes-pdf/${inf.id}/descargar`);
  const borrar = async (inf) => {
    if (!window.confirm(`¿Quitar el informe «${inf.filename}»?`)) return;
    await api.delete(`/informes-pdf/${inf.id}`);
    load();
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="informes-pdf">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-semibold text-slate-800 flex-1">Informes en PDF</p>
        <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => subir(e.target.files[0])} data-testid="informe-file-input" />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid="informe-subir-btn"><FileUp className="h-4 w-4 mr-1" />{busy ? "Subiendo…" : "Subir informe"}</Button>
      </div>
      {rows.length ? (
        <div className="divide-y divide-slate-100">
          {rows.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs" data-testid={`informe-${i.id}`}>
              <span className="flex-1 min-w-[160px] truncate font-medium text-slate-800">{i.filename}</span>
              <span className="text-slate-500">{fmtDate(i.created_at.slice(0, 10))}{i.subido_por ? ` · ${i.subido_por}` : ""}</span>
              {i.enviado_at ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Enviado {fmtDate(i.enviado_at.slice(0, 10))} a {i.enviado_a}</span> : <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">Sin enviar</span>}
              <Button size="sm" variant="ghost" onClick={() => ver(i)} data-testid={`informe-ver-${i.id}`}><Eye className="h-3.5 w-3.5" /></Button>
              {i.enviado_at && <Button size="sm" variant="ghost" className="text-emerald-700" title="Avisar por WhatsApp" disabled={!patient.telefono} onClick={() => avisarWa(patient, i.enviado_a, false)} data-testid={`informe-wa-${i.id}`}><MessageCircle className="h-3.5 w-3.5" /></Button>}
              <Button size="sm" variant="outline" onClick={() => setEnv(i)} data-testid={`informe-enviar-${i.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Enviar</Button>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => borrar(i)} data-testid={`informe-borrar-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              <Envios inf={i} />
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-slate-400">Aún no hay informes subidos. Sube el PDF terminado y envíalo desde aquí.</p>}
      <EnviarDialog inf={env} patient={patient} onClose={() => setEnv(null)} onDone={() => { setEnv(null); load(); onChange(); }} />
    </div>
  );
};
